// ABOUTME: Middleware that fetches userLayout from the backend on environment switches.
// ABOUTME: GET + hydrate on env switch; prompts user to save if remote is empty but local has config.

import userLayoutSlice from "../global/userLayout/userLayoutSlice";
import { tableSettingsSlice } from "../global/tableSettings/tableSettingsSlice";
import settingsSlice from "../global/settingsSlice";
import { ENV_TYPES } from "../../components/global/Sidebar";

// Environment switch actions that should trigger a fresh GET.
const ENV_SWITCH_ACTION_TYPES = new Set([
    settingsSlice.actions.setSelectedEnvironment.type,
    settingsSlice.actions.setSelectedInnerEnvironment.type,
]);

/**
 * Check whether a userLayout document has any meaningful config data.
 * An empty document (no column configs, no tab configs) is treated as "nothing to save".
 */
const hasLocalConfig = (document) => {
    if (!document) return false;
    const hasColumnConfigs = document.columnConfigs
        && Object.keys(document.columnConfigs).length > 0;
    const hasTabConfigs = document.tabConfigs
        && Object.keys(document.tabConfigs).length > 0;
    return hasColumnConfigs || hasTabConfigs;
};

/**
 * Fetch table settings from the backend and hydrate userLayoutSlice.
 * The prompt to save only fires when switching within the same environment
 * type (warehouse→warehouse or relation→relation). Cross-type switches
 * (warehouse→relation or relation→warehouse) never prompt.
 */
const fetchAndHydrate = async (storeAPI, sameEnvType) => {
    const localDocumentBefore = storeAPI.getState().userLayout?.document;
    const localHasConfig = hasLocalConfig(localDocumentBefore);

    let remoteFound = false;

    try {
        const result = await storeAPI.dispatch(
            tableSettingsSlice.endpoints.getTableSettings.initiate()
        ).unwrap();

        if (result && result.success !== false) {
            storeAPI.dispatch(
                userLayoutSlice.actions.hydrate({
                    columnConfigs: result.columnConfigs ?? {},
                    generalSettings: result.generalSettings ?? {},
                    tabConfigs: result.tabConfigs ?? {},
                })
            );
            remoteFound = true;
        }
    } catch {
        // 404 or network error — the environment has no saved settings yet.
    }

    if (!remoteFound) {
        // Reset to empty so stale data from the previous environment doesn't leak.
        storeAPI.dispatch(userLayoutSlice.actions.hydrate({
            columnConfigs: {},
            generalSettings: {},
            tabConfigs: {},
        }));

        // Only prompt when staying within the same env type, local has config,
        // and the environment is not a company env (company envs never prompt).
        const envType = storeAPI.getState().settings?.environment?.selectedEnvironmentType;
        if (sameEnvType && localHasConfig && envType !== ENV_TYPES[100]) {
            storeAPI.dispatch(
                userLayoutSlice.actions.showSaveToNewEnvPrompt({
                    pendingDocument: localDocumentBefore,
                })
            );
        }
    }
};

export const tableSettingsMiddleware = (storeAPI) => (next) => (action) => {
    // Capture env type BEFORE the reducer processes the action.
    const envTypeBefore = storeAPI.getState().settings?.environment?.selectedEnvironmentType;

    const result = next(action);

    if (ENV_SWITCH_ACTION_TYPES.has(action.type)) {
        const envTypeAfter = storeAPI.getState().settings?.environment?.selectedEnvironmentType;
        const sameEnvType = envTypeBefore === envTypeAfter;

        // Cross-type switch (e.g. relation → fulfilment): clear local config
        // immediately so stale data from the previous env type can't leak into
        // future same-type switches.
        if (!sameEnvType) {
            storeAPI.dispatch(userLayoutSlice.actions.hydrate({
                columnConfigs: {},
                generalSettings: {},
                tabConfigs: {},
            }));
        }

        fetchAndHydrate(storeAPI, sameEnvType);
    }

    return result;
};
